import React from 'react';
import { useQuery } from 'react-query';
import { standingsService } from '@/services/standingsService';
import { playerService } from '@/services/playerService';
import ContextualStandingsTable from './ContextualStandingsTable';
import PlayerTeamInfo from './PlayerTeamInfo';

interface LeagueStandingsSectionProps {
  leagueId: string;
  playerAssignments: Array<{
    playerId?: string;
    player?: { name: string };
    teamName: string;
  }>;
  description?: string;
  leagueName?: string;
  refreshTrigger?: number;
  league?: {
    playoffsEnabled?: boolean;
    playoffSpots?: number;
    standingsSortBy?: string;
    standingsSource?: string;
  };
}

const LeagueStandingsSection: React.FC<LeagueStandingsSectionProps> = ({
  leagueId,
  playerAssignments,
  description,
  leagueName,
  refreshTrigger = 0,
  league
}) => {

  const hasPlayerAssigned = playerAssignments && playerAssignments.length > 0;

  // Get players data to ensure we have names
  const { data: playersData } = useQuery(
    ['players', hasPlayerAssigned],
    () => playerService.getPlayers(),
    {
      enabled: hasPlayerAssigned,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );

  // Get user team names
  const userTeams = playerAssignments?.map(assignment => assignment.teamName) || [];

  // Use a stable string key for userTeams to prevent unnecessary cache misses
  const userTeamsKey = userTeams.sort().join(',');

  // Single API call: contextual standings + unverified deltas + next match + full standings
  // Everything resolves atomically — standings never render without unverified indicators.
  const {
    data: combinedData,
    isLoading,
    error
  } = useQuery(
    ['league-standings-combined', leagueId, userTeamsKey, hasPlayerAssigned, refreshTrigger, league?.standingsSource],
    async () => {
      if (hasPlayerAssigned && userTeams.length > 0) {
        // One API call returns contextual standings + requested fields
        const response = await standingsService.getContextualStandings(
          leagueId, userTeams, undefined, 5,
          ['unverifiedDeltas', 'fullStandings']
        );

        const fullStandings = response.fullStandings || [];
        const divisions = [...new Set(fullStandings.map(team => team.division || 'Standings').filter(Boolean))];

        let contextualStandings;
        if (divisions.length > 1) {
          const userTeam = response.contextualStandings.find(team => team.isUserTeam);
          const userDivision = userTeam?.division;

          const divisionMap = new Map<string, any[]>();
          fullStandings.forEach(team => {
            const div = team.division || 'Standings';
            if (!divisionMap.has(div)) divisionMap.set(div, []);
            divisionMap.get(div)!.push(team);
          });

          const sortedDivisions = userDivision
            ? [userDivision, ...divisions.filter(d => d !== userDivision)]
            : divisions;

          contextualStandings = sortedDivisions.flatMap(division => {
            if (division === userDivision) {
              return response.contextualStandings.filter(team =>
                (team.division || 'Standings') === division
              );
            } else {
              const divisionTeams = divisionMap.get(division) || [];
              return divisionTeams.slice(0, 5).map(team => ({
                ...team,
                isUserTeam: false
              }));
            }
          });
        } else {
          contextualStandings = response.contextualStandings;
        }

        return {
          standings: { contextualStandings },
          unverifiedDeltas: response.unverifiedDeltas || {},
        };
      } else {
        // No player assigned — get full standings only (no extras needed for next match)
        const data = await standingsService.getStandings(leagueId);
        const divisionMap = new Map<string, any[]>();
        data.standings?.forEach(team => {
          const div = team.division || 'Standings';
          if (!divisionMap.has(div)) divisionMap.set(div, []);
          divisionMap.get(div)!.push(team);
        });

        const top5PerDivision = Array.from(divisionMap.values())
          .flatMap(teams => teams.slice(0, 5));

        return {
          standings: {
            contextualStandings: top5PerDivision.map(team => ({
              ...team,
              isUserTeam: false
            }))
          },
          unverifiedDeltas: {} as Record<string, { played: number; won: number; drawn: number; lost: number }>,
        };
      }
    },
    {
      enabled: true,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      retry: 1
    }
  );

  const standingsData = combinedData?.standings;
  const unverifiedDeltas = combinedData?.unverifiedDeltas || {};

  // Get all players for display
  const allPlayers = playerAssignments || [];
  // Get player names from players data
  const playerNames = hasPlayerAssigned
    ? allPlayers.map(assignment =>
        assignment?.player?.name ||
        (assignment?.playerId && playersData?.find(p => p.id === assignment.playerId)?.name) ||
        'Player'
      ).filter(Boolean)
    : [];

  const displayName = playerNames.length > 0 ? playerNames.join(', ') : 'None';

  return (
    <>
      {/* Player Team Info - always show */}
      <PlayerTeamInfo
        playerName={displayName}
        description={description}
        leagueName={leagueName}
      />

      {/* Contextual Standings Table */}
      <ContextualStandingsTable
        standings={standingsData?.contextualStandings || []}
        loading={isLoading}
        error={error ? 'Unable to load standings' : undefined}
        league={league}
        leagueId={leagueId}
        unverifiedDeltas={Object.keys(unverifiedDeltas).length > 0 ? unverifiedDeltas : undefined}
      />
    </>
  );
};

export default React.memo(LeagueStandingsSection);
